import {
  Badge,
  Button,
  createTableColumn,
  Dropdown,
  Input,
  makeStyles,
  Option,
  type TableColumnDefinition,
  Text,
  Toolbar,
  tokens,
} from "@fluentui/react-components"
import { ArrowDownloadRegular, HistoryRegular, SearchRegular } from "@fluentui/react-icons"
import type { AuditLog, AuditLogPage, AuditSource } from "@rema/shared/schemas/audit"
import { useState } from "react"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { PageHeader } from "../../components/page-header"
import { api, getAccessToken } from "../../lib/api"
import { auditI18n as i18n } from "./i18n"

const useStyles = makeStyles({
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  search: { minWidth: "220px" },
  spacer: { marginLeft: "auto" },
  mono: { fontFamily: tokens.fontFamilyMonospace },
})

const QUERY_KEY = ["admin", "audit"] as const
type SourceFilter = AuditSource | "all"

const fetchAudit = (cursor: string | null, event: string, actor: string, source: SourceFilter) =>
  api.get<AuditLogPage>("/admin/audit", {
    query: {
      cursor: cursor ?? undefined,
      event: event.trim() || undefined,
      actor: actor.trim() || undefined,
      source: source === "all" ? undefined : source,
    },
  })

export const AuditPage = () => {
  const styles = useStyles()
  const [event, setEvent] = useState("")
  const [actor, setActor] = useState("")
  const [source, setSource] = useState<SourceFilter>("all")

  const exportCsv = async () => {
    const params = new URLSearchParams()
    if (event.trim()) params.set("event", event.trim())
    if (actor.trim()) params.set("actor", actor.trim())
    if (source !== "all") params.set("source", source)
    const token = getAccessToken()
    const res = await fetch(`/v1/admin/audit/export?${params.toString()}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) return
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "audit.csv"
    link.click()
    URL.revokeObjectURL(url)
  }

  const columns: TableColumnDefinition<AuditLog>[] = [
    createTableColumn<AuditLog>({
      columnId: "occurredAt",
      renderHeaderCell: () => i18n.columns.occurredAt,
      renderCell: (log) => new Date(log.occurred_at).toLocaleString(),
    }),
    createTableColumn<AuditLog>({
      columnId: "event",
      renderHeaderCell: () => i18n.columns.event,
      renderCell: (log) => <Text className={styles.mono}>{log.event}</Text>,
    }),
    createTableColumn<AuditLog>({
      columnId: "actor",
      renderHeaderCell: () => i18n.columns.actor,
      renderCell: (log) => `${log.actor_type}${log.actor_id ? `:${log.actor_id}` : ""}`,
    }),
    createTableColumn<AuditLog>({
      columnId: "source",
      renderHeaderCell: () => i18n.columns.source,
      renderCell: (log) => (
        <Badge appearance="outline" color={log.source === "external" ? "warning" : "informative"}>
          {i18n.sources[log.source]}
        </Badge>
      ),
    }),
    createTableColumn<AuditLog>({
      columnId: "target",
      renderHeaderCell: () => i18n.columns.target,
      renderCell: (log) => (log.target_type ? `${log.target_type}:${log.target_id ?? ""}` : "—"),
    }),
  ]

  const toolbar = (
    <Toolbar className={styles.toolbar} aria-label={i18n.title}>
      <Input
        className={styles.search}
        value={event}
        onChange={(_, d) => setEvent(d.value)}
        placeholder={i18n.searchEvent}
        contentBefore={<SearchRegular />}
        aria-label={i18n.searchEvent}
      />
      <Input
        className={styles.search}
        value={actor}
        onChange={(_, d) => setActor(d.value)}
        placeholder={i18n.searchActor}
        aria-label={i18n.searchActor}
      />
      <Dropdown
        value={i18n.sources[source === "all" ? "all" : source]}
        selectedOptions={[source]}
        onOptionSelect={(_, d) => setSource((d.optionValue as SourceFilter) ?? "all")}
        aria-label={i18n.filterSource}
      >
        <Option value="all">{i18n.sources.all}</Option>
        <Option value="internal">{i18n.sources.internal}</Option>
        <Option value="external">{i18n.sources.external}</Option>
      </Dropdown>
      <Button
        className={styles.spacer}
        icon={<ArrowDownloadRegular />}
        onClick={() => void exportCsv()}
      >
        {i18n.export}
      </Button>
    </Toolbar>
  )

  return (
    <>
      <PageHeader title={i18n.title} description={i18n.description} />
      <DataGridPage<AuditLog>
        queryKey={[...QUERY_KEY, event.trim(), actor.trim(), source]}
        fetchPage={(cursor) => fetchAudit(cursor, event, actor, source)}
        columns={columns}
        getRowId={(log) => log.id}
        toolbar={toolbar}
        emptyState={
          <EmptyState
            icon={<HistoryRegular />}
            title={i18n.empty.title}
            description={i18n.empty.description}
          />
        }
      />
    </>
  )
}
